import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { APP_TITLE } from "../../../config/constant";
import PopupContext from "../../../PopupContext";
import { defaultStatusColors } from "../../../utils/CardUtils";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import InfinityGrid from "../../common/cardContainers/InfinityGrid.jsx";
import ReordersGridCard from "../../common/gridCards/ReordersGridCard.jsx";
import ListComponent from "../../common/lists/ListComponent";
import ContainerLoader from "../../common/loaders/ContainerLoader";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import ReorderSuggestionPopup from "./reordersComponents/ReorderSuggestionPopup.jsx";
import ReordersExpandedComponent from "./reordersComponents/ReordersExpandedComponent.jsx";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }

    .table-structure.expandable-rows .rdt_TableHeadRow > div:first-child,
    .table-structure.expandable-rows .rdt_TableRow > div:first-child,
    .table-structure.expandable-rows .rdt_ExpanderRow .nested-row > .nested-col:first-child {
        width: 20px !important;
        flex: 0 0 20px !important;
        min-width: 20px !important;
        margin-left: 8px !important;
    }
`;
// MISSING:
// .details-item {
//      height: 9px !important;
// }
// #endregion

export default function ReordersPage() {
    document.title = `${APP_TITLE} - Reorders`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [listNumRows, setListNumRows] = useState(10);
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const gridRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    // #endregion

    // #region STATES
    const [searchText, setSearchText] = useState("");
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [filteredRetailBrand, setFilteredRetailBrand] = useState([]);
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    const [allStatusList, setAllStatusList] = useState([
        { name: "New", value: "New", selected: true },
        { name: "Declined", value: "DeclinedByRetailBrand", selected: true },
        { name: "Ongoing", value: "Ongoing", selected: true },
        { name: "Accepted, not yet sent", value: "AcceptedByRetailBrand", selected: true },
        { name: "Sent", value: "Shared", selected: true },
        { name: "Declined by label brand", value: "DeclinedByLabelBrand", selected: true },
        { name: "Processing", value: "AcceptedByLabelBrand", selected: true },
        { name: "Processed", value: "Processed", selected: true }
    ]);
    const [filteredStatus, setFilteredStatus] = useState([]);
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const filters = {
        retailBrandIDs: filteredRetailBrand?.map(item => item?.value),
        labelBrandIDs: filteredLabelBrand?.map(item => item?.value),
        searchText: searchText,
        statuses: filteredStatus?.map(item => item?.value)
    };
    const listQueryKey = ["OrdersList", filters];

    const { showReorderSuggestionPopup, setShowReorderSuggestionPopup } = useContext(PopupContext);

    const [selectedOrderItem, setSelectedOrderItem] = useState({
        item: [],
        supplierOrderID: "",
        reOrderNumber: "",
        status: "",
        createdAt: "",
        userName: "",
        retailBrandName: "",
        labelBrandName: ""
    });

    const [listComponentIsLoading, setListComponentIsLoading] = useState(false);
    const [reorderDetailsLoader, setReorderDetailsLoader] = useState(false);

    const infiniteGridQueryKey = ["ReordersGrid", filters];
    // #endregion

    // #region METHODS
    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            gridRef.current?.reset?.();
        }
    };

    const onSearch = text => {
        setSearchText(text);
    };

    const onRetailBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredRetailBrand(selections);
        setAllRetailBrandsList(dropdownDataWithSelections);
    };

    const onLabelBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredLabelBrand(selections);
        setAllLabelBrandsList(dropdownDataWithSelections);
    };

    const onStatusChange = (selections, dropdownDataWithSelections) => {
        setFilteredStatus(selections);
        setAllStatusList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        // const defaultRetailBrand = sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");
        // const defaultLabelBrand = sessionStorage.getItem("currentLabelBrand") || localStorage.getItem("defaultLabelBrand");
        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }
                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultRetailBrand
                };
            });
            //const defaultRetailBrandObj = retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setFilteredRetailBrand(retailBrands);
        }

        if (data?.connectedLabelBrands?.length) {
            const labelBrands = data?.connectedLabelBrands?.map((item, index) => {
                const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
                let labelBrandName = item?.name;

                if (item?._id === defaultLabelBrandID) {
                    labelBrandName = `${item?.name} (default)`;
                }
                return {
                    name: labelBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultLabelBrand
                };
            });
            //const defaultLabelBrandObj = labelBrands?.find(item => item?.value === defaultLabelBrand) || labelBrands?.[0];

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand(labelBrands);
        }

        setRequestsEnabled(true);

        // OLD
        // if (data?.customFilters) {
        //     const statuses = data?.customFilters?.map(item => {
        //         return {
        //             name: item?.name,
        //             value: item?._id,
        //             selected: true
        //         };
        //     });

        //     setFilteredStatus(statuses?.filter(item => item?.selected));
        // }
    };

    const handleViewReorder = async ({ supplierOrderID }) => {
        setSelectedOrderItem({
            item: [],
            supplierOrderID: "",
            reOrderNumber: "",
            status: "",
            createdAt: "",
            userName: "",
            retailBrandName: "",
            labelBrandName: ""
        });
        if (!supplierOrderID) return;

        try {
            const res = await Library.makeCommonPostRequest(
                "SupplierOrders",
                "getReorderDetails",
                {
                    supplierOrderID: supplierOrderID
                },
                false,
                setReorderDetailsLoader
            );

            if (res?.data?.status === 200) {
                setSelectedOrderItem({ ...res?.data?.data });
                setShowReorderSuggestionPopup(true);
            }
        } catch (error) {
            console.error("Error fetching reorder details:", error);
        }
    };

    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Style code",
            selector: row => row?.styleCode ?? "--"
        },
        {
            name: "Style name",
            selector: row => row?.styleName ?? "--"
        },
        {
            name: "Reorder nr.",
            selector: row => row?.reorderNumber ?? "--"
        },
        {
            name: "From brand",
            selector: row => row?.retailBrandName ?? "--"
        },
        {
            name: "To brand",
            selector: row => row?.labelBrandName ?? "--"
        },
        {
            name: "Action logs",
            selector: row => "--"
        },
        {
            name: "Date",
            selector: row => formatDefaultDateTime(row?.date) ?? "--"
        },
        {
            name: "Total items",
            selector: row => row?.totalItems ?? "--"
        }
    ];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);

        setupHeaders(
            props,
            onClickRefreshAction,
            onSearch,
            onFiltersLoaded,
            allRetailBrandsList,
            onRetailBrandChange,
            filteredRetailBrand,
            allLabelBrandsList,
            onLabelBrandChange,
            filteredLabelBrand,
            allStatusList,
            onStatusChange,
            filteredStatus
        );
    }, [props?.layoutType, allRetailBrandsList, filteredRetailBrand, allLabelBrandsList, filteredLabelBrand]);

    // OLD
    useEffect(() => {
        if (location.state?.showReorderPopup && location.state?.supplierOrderID) {
            handleViewReorder({ supplierOrderID: location.state.supplierOrderID });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="reorders-page">
            <FullScreenLoader isLoading={reorderDetailsLoader} />

            <FullScreenOverlay show={showReorderSuggestionPopup}>
                <ReorderSuggestionPopup
                    data={selectedOrderItem}
                    handleClosePopup={() => setShowReorderSuggestionPopup(false)}
                    reordersPageRefresh={onClickRefreshAction}
                />
            </FullScreenOverlay>

            {viewLayoutStyle === "Grid" && (
                <InfinityGrid
                    ref={gridRef}
                    pageCard={ReordersGridCard}
                    queryKey={infiniteGridQueryKey}
                    minColumnWidth="238px"
                    imageHeight="69px"
                    skeletonHeight="485.62px"
                    endpointName="SupplierOrders/getReordersData"
                    payload={{ companyID: localStorage.getItem("currentCompanyID"), limit: 12, filters }}
                    requestEnabled={requestEnabled}
                    getItemKey={item => item?._id}
                    secondaryButtonOnClick={item => {
                        handleViewReorder({ supplierOrderID: item?._id });
                    }}
                    thirdButtonOnClick={item => {
                        navigate(`/products/metrics?productID=${item?.productID}&productName=${item?.styleName}`);
                    }}

                    // OLD
                    // secondaryButtonOnClick={item => {
                    //     handleShowOrderPreview(item);
                    // }}
                    // highlighted={props?.highlighted}
                    // highlightColor={props?.highlightColor}
                    // highlightCustomText={props?.highlightCustomText}
                />
            )}

            {viewLayoutStyle === "List" && (
                <ContainerLoader
                    isLoading={listComponentIsLoading}
                    className="w-100"
                    isLoadingOverlay={true}
                    addBlurFilters={false}>
                    <ListComponent
                        className="w-100"
                        tableClassName="expandedRowsCentered"
                        title="REORDERS"
                        columns={listColumns}
                        selectableRows={true}
                        showLoader={false}
                        onListLoading={loading => setListComponentIsLoading(loading)}
                        api={{
                            useCommonPostRequest: true,
                            commonContext: "SupplierOrders",
                            endpoint: "getReordersData",
                            formData: {
                                companyID: localStorage.getItem("currentCompanyID"),
                                limit: listNumRows,
                                filters: filters
                            }
                        }}
                        pageLimit={listNumRows}
                        currentPage={1}
                        queryKey={"OrdersList"}
                        actions={{
                            includeActions: true,
                            editIsVisible: false,
                            suspendIsVisible: false,
                            removeIsVisible: false,
                            extraActions: [
                                {
                                    title: "View",
                                    onClick: item => {
                                        setShowReorderSuggestionPopup(true);
                                        handleViewReorder({ supplierOrderID: item?._id });
                                    }
                                }
                            ]
                        }}
                        includeStatus={{
                            stickyStatus: true,
                            statusFromPath: "status",
                            statusColors: defaultStatusColors
                        }}
                        expandableRows={true}
                        customExpandedComponent={item => <ReordersExpandedComponent data={item?.data} />}
                        requestEnabled={requestEnabled}
                        onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    />
                </ContainerLoader>
            )}
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders(
    props,
    onClickRefreshAction,
    onSearch,
    onFiltersLoaded,
    allRetailBrandsList,
    onRetailBrandChange,
    filteredRetailBrand,
    allLabelBrandsList,
    onLabelBrandChange,
    filteredLabelBrand,
    allStatusList,
    onStatusChange,
    filteredStatus
) {
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Order number, brand" /> }
    ]);

    props.setControlBarCenterBtns([
        { to: "/orders", active: false, disabled: false, title: "ORDERS" },
        { to: "/orders/reorders", active: true, disabled: false, title: "REORDERS" },
        { to: "/orders/archive", active: false, disabled: false, title: "ARCHIVE" }
    ]);

    props.setControlBarRightBtns([
        { to: "/orders/reorders", active: true, disabled: false, title: "View", icon: "eye" },
        { to: "/orders/reorders/calendar", active: false, disabled: true, title: "Calendar", icon: "calendar" },
        { to: "/orders/reorders/details", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                onRefreshClick={onClickRefreshAction}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                firstOrganize={{
                    visible: true,
                    dropdownData: allLabelBrandsList,
                    onDropdownChange: onLabelBrandChange,
                    disabled: allLabelBrandsList?.length === 1,
                    type: allLabelBrandsList?.length === 1 ? "singleSelect" : "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all label brands",
                        labelAllSelected: "All label brands",
                        labelNoneSelected: "--",
                        allItems: allLabelBrandsList
                    }
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allStatusList,
                    onDropdownChange: onStatusChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all status",
                        labelAllSelected: "All status",
                        labelNoneSelected: "--",
                        allItems: allStatusList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useConnectedLabelBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true
                }}
            />
        )
    });
}
// #endregion
